/**
 * Veilleur de connexion de l'écran (TV) : détecte un socket « mort mais qui se
 * croit vivant » et force sa reconnexion en ~10-15 s au lieu de ~40-60 s.
 *
 * Pourquoi : quand le wifi d'un bar cale quelques secondes, la connexion TCP de
 * l'écran peut mourir sans bruit. Socket.IO ne s'en aperçoit qu'au bout de
 * pingInterval + pingTimeout (10 + 30 s côté serveur), alors que le réseau est
 * souvent revenu bien avant : la salle voit l'écran figé 40 à 66 s (boîte noire,
 * 21 j au 12/10/2026 : 9 soirées sur 144 > 15 s, 6 > 30 s ; SAS J&T, TVC, Pro
 * Concept…). Le serveur sait déjà accueillir l'écran qui revient alors que son
 * ancien socket est encore enregistré (updateSID : remplace l'ancien, les
 * manettes ne voient qu'un `device_back`).
 *
 * Principe : un écho écran → serveur → écran toutes les PROBE_MS. Lien déclaré
 * mort si, depuis STALE_MS, le plus ancien écho est sans réponse ET plus aucun
 * paquet n'est arrivé (dans une salle active le trafic entrant est continu : un
 * écho seulement en retard derrière une file de messages ne compte pas). Avant
 * d'agir, `confirmAndReconnect` (composant) attend un délai aléatoire puis vérifie
 * que le SERVEUR répond en HTTP : s'il ne répond pas, c'est lui (ou tout le
 * réseau) qui cale, et socket.io s'en chargera — sinon un serveur figé 10-40 s
 * déclencherait tout le parc à la fois. Sinon, on ferme la connexion comme le
 * ferait socket.io sur un « ping timeout » : reconnexion automatique et reprise
 * de session de la lib (chemin déjà emprunté à chaque coupure réseau).
 *
 * Garde-fous contre les faux positifs et les tempêtes :
 * - « lié » = socket connecté ET session reprise (`ready` reçu depuis le dernier
 *   `connect`) : jamais pendant une reprise en cours ; après TOUTE (re)liaison,
 *   naturelle ou forcée, COOLDOWN_MS de silence ;
 * - onglet caché, boucle JS figée (tick en retard > FREEZE_MS) : échos oubliés ;
 * - au plus MAX_FORCED reconnexions par BUDGET_WINDOW_MS (mémorisé hors de la
 *   page : survit aux rechargements et remontages), et aucune si la précédente
 *   date de moins de RETRY_GAP_MS (elle n'a rien réglé : on n'insiste pas).
 *
 * Classe PURE : horloges, stockage et dépendances injectés (tests). Le
 * branchement sur la lib et le socket brut vit dans `ScreenStaleLinkReconnect`.
 */

export type StaleLinkInfo = { staleMs: number; acted: boolean; reason?: 'budget' | 'retry_gap' | 'not_confirmed' };

export type StaleLinkDeps = {
    /** Horloge monotone (performance.now) : immune aux recalages NTP des box TV. */
    now: () => number;
    /** Horloge murale (Date.now) : seulement pour le budget mémorisé entre pages. */
    wallNow: () => number;
    /** Socket connecté ET session reprise (ready reçu depuis le dernier connect). */
    isLinked: () => boolean;
    isHidden: () => boolean;
    /** Heure (horloge `now`) du dernier paquet reçu du serveur, s'il y en a eu. */
    lastIncomingAt: () => number | undefined;
    /** Envoie un écho à soi-même via le serveur. Peut lever (device pas prêt). */
    sendProbe: (id: number) => void;
    /** Délai aléatoire + serveur joignable ? → ferme la connexion. Résout `true` si fermée. */
    confirmAndReconnect: () => Promise<boolean>;
    /** Budget mémorisé hors de la page (sessionStorage). */
    loadForcedAt: () => number[];
    saveForcedAt: (wallTimes: number[]) => void;
    /** Signal de mesure (GA4, Sentry, boîte noire). Ne doit jamais bloquer la reconnexion. */
    onStale?: (info: StaleLinkInfo) => void;
};

export class StaleLinkWatchdog {
    static readonly TICK_MS = 1000;

    static readonly PROBE_MS = 5000;

    /** ~60× la latence habituelle (p50 ≈ 170 ms) : un vrai silence, pas un réseau lent. */
    static readonly STALE_MS = 10_000;

    /** Écart entre deux ticks au-delà duquel on considère la boucle JS figée. */
    static readonly FREEZE_MS = 3000;

    /** Silence après toute (re)liaison : la reprise de session et sa rafale se tassent. */
    static readonly COOLDOWN_MS = 15_000;

    static readonly MAX_FORCED = 3;

    static readonly BUDGET_WINDOW_MS = 30 * 60 * 1000;

    /** Une reconnexion forcée suivie d'un nouveau silence si tôt n'a rien réglé. */
    static readonly RETRY_GAP_MS = 2 * 60 * 1000;

    private readonly _deps: StaleLinkDeps;

    /** id → heure d'envoi des échos sans réponse (ordre d'insertion = ordre d'envoi). */
    private _inFlight = new Map<number, number>();

    private _nextId = 1;

    private _lastTick?: number;

    private _lastProbe?: number;

    private _wasLinked = false;

    private _quietUntil = 0;

    private _confirming = false;

    private _staleReported = false;

    constructor(deps: StaleLinkDeps) {
        this._deps = deps;
    }

    /** Appelé toutes les TICK_MS. */
    tick() {
        const { now, isLinked, isHidden } = this._deps;
        const t = now();
        const frozen = this._lastTick !== undefined && t - this._lastTick > StaleLinkWatchdog.FREEZE_MS;
        this._lastTick = t;
        if (this._confirming) {
            return;
        }

        const linked = isLinked();
        if (linked && !this._wasLinked) {
            // (Re)liaison, naturelle ou forcée : on laisse la reprise se tasser.
            this._quietUntil = t + StaleLinkWatchdog.COOLDOWN_MS;
            this._reset();
        }
        this._wasLinked = linked;

        if (frozen || isHidden() || !linked) {
            // Boucle figée, onglet caché ou socket déjà tombé (socket.io gère) :
            // les échos en vol ne disent rien de l'état du lien.
            this._reset();
            return;
        }
        if (t < this._quietUntil) {
            return;
        }

        const oldest = this._inFlight.values().next();
        if (!oldest.done && t - oldest.value >= StaleLinkWatchdog.STALE_MS) {
            const lastIn = this._deps.lastIncomingAt();
            if (lastIn === undefined || t - lastIn >= StaleLinkWatchdog.STALE_MS) {
                this._stale(t, t - oldest.value);
                return;
            }
        }

        if (this._lastProbe === undefined || t - this._lastProbe >= StaleLinkWatchdog.PROBE_MS) {
            this._probe(t);
        }
    }

    /** Un écho est revenu : le lien vit. Les échos plus anciens sont réputés perdus en route. */
    noteEcho(id: number) {
        if (!this._inFlight.has(id)) {
            return;
        }
        for (const key of [...this._inFlight.keys()]) {
            this._inFlight.delete(key);
            if (key === id) break;
        }
        this._staleReported = false;
    }

    private _probe(t: number) {
        this._lastProbe = t;
        const id = this._nextId;
        this._nextId += 1;
        try {
            this._deps.sendProbe(id);
            this._inFlight.set(id, t);
        } catch {
            // Device pas encore enregistré (gotcha #138) : pas d'écho attendu.
        }
    }

    private _report(info: StaleLinkInfo) {
        try {
            this._deps.onStale?.(info);
        } catch {
            // Une mesure ne doit jamais casser la reconnexion.
        }
    }

    private _stale(t: number, staleMs: number) {
        const wall = this._deps.wallNow();
        const forcedAt = this._deps
            .loadForcedAt()
            .filter((at) => wall - at >= 0 && wall - at < StaleLinkWatchdog.BUDGET_WINDOW_MS);
        const last = forcedAt.length ? Math.max(...forcedAt) : undefined;
        let reason: StaleLinkInfo['reason'];
        if (forcedAt.length >= StaleLinkWatchdog.MAX_FORCED) reason = 'budget';
        else if (last !== undefined && wall - last < StaleLinkWatchdog.RETRY_GAP_MS) reason = 'retry_gap';

        if (reason) {
            // On observe sans agir : un signal par épisode de silence, et on continue
            // de sonder (le lien peut revenir seul).
            if (!this._staleReported) {
                this._staleReported = true;
                this._report({ staleMs, acted: false, reason });
            }
            if (this._lastProbe === undefined || t - this._lastProbe >= StaleLinkWatchdog.PROBE_MS) {
                this._probe(t);
            }
            return;
        }

        this._confirming = true;
        this._reset();
        let settled: Promise<boolean>;
        try {
            settled = this._deps.confirmAndReconnect();
        } catch {
            settled = Promise.resolve(false);
        }
        settled
            .catch(() => false)
            .then((acted) => {
                this._confirming = false;
                if (acted) {
                    this._deps.saveForcedAt([...forcedAt, this._deps.wallNow()]);
                    // Le socket tombe : la (re)liaison suivante ouvrira le cooldown.
                    this._wasLinked = false;
                } else {
                    // Serveur injoignable ou lien revenu entre-temps : socket.io gère.
                    this._quietUntil = this._deps.now() + StaleLinkWatchdog.COOLDOWN_MS;
                }
                this._report({ staleMs, acted, ...(acted ? {} : { reason: 'not_confirmed' as const }) });
            });
    }

    private _reset() {
        this._inFlight.clear();
        this._lastProbe = undefined;
        this._staleReported = false;
    }
}

/* Relais vers la boîte noire de la partie en cours (FirestoreScreenDataCollector,
 * un par jeu) : le veilleur vit toute la page, la boîte noire seulement pendant un jeu. */
type StaleLinkListener = (info: StaleLinkInfo) => void;

const staleLinkListeners = new Set<StaleLinkListener>();

export function onStaleLink(listener: StaleLinkListener): () => void {
    staleLinkListeners.add(listener);
    return () => {
        staleLinkListeners.delete(listener);
    };
}

export function emitStaleLink(info: StaleLinkInfo) {
    staleLinkListeners.forEach((listener) => {
        try {
            listener(info);
        } catch {
            // Une mesure ne doit jamais casser la reconnexion.
        }
    });
}
